import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getDomains } from '../../service/api/domainService';
import { getValidationRule ,createOrUpdateValidationRule} from '../../service/api/validation-service';
import SearchableSelect from '../../SearchableSelect';


const ValidationRulesManager = () => {
  const queryClient = useQueryClient();
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState('');

  const entityTypes = [
    { value: 'Order', label: 'Ordre de Bourse' },
    { value: 'Account', label: 'Compte Utilisateur' },
    { value: 'Transaction', label: 'Transaction' },
  ];

  // Chargement des domaines
  const { data: domains = [] } = useQuery({
    queryKey: ['domains'],
    queryFn: getDomains,
  });

  // Chargement de la règle existante
  const { data: existingRule, isLoading: loadingRule } = useQuery({
    queryKey: ['validation-rule', selectedDomain?.id, selectedEntity],
    queryFn: () => {
      if (selectedDomain && selectedEntity) {
        return getValidationRule(selectedDomain.id, selectedEntity);
      }
      return null;
    },
    enabled: !!selectedDomain && !!selectedEntity,
  });

  // Mutation pour sauvegarder
  const saveRuleMutation = useMutation({
    mutationFn: createOrUpdateValidationRule,
    onSuccess: () => {
      toast.success('Règle sauvegardée avec succès');
      queryClient.invalidateQueries(['validation-rule', selectedDomain?.id, selectedEntity]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    },
  });

  const [formData, setFormData] = useState({
    approvalType: 'Any',
    approvalThreshold: null,
    isActive: true,
  });

  // Mettre à jour le formData quand une règle existe
  useEffect(() => {
    if (existingRule) {
      setFormData({
        approvalType: existingRule.approvalType,
        approvalThreshold: existingRule.approvalThreshold,
        isActive: existingRule.isActive,
      });
    } else {
      setFormData({
        approvalType: 'Any',
        approvalThreshold: null,
        isActive: true,
      });
    }
  }, [existingRule]);

  const handleSaveRule = () => {
    if (!selectedDomain || !selectedEntity) {
      toast.error('Veuillez sélectionner un domaine et une entité');
      return;
    }

    if (formData.approvalType === 'Quorum' && !formData.approvalThreshold) {
      toast.error('Le seuil est obligatoire pour le type Quorum');
      return;
    }

    saveRuleMutation.mutate({
      domainId: selectedDomain.id,
      entityName: selectedEntity,
      approvalType: formData.approvalType,
      approvalThreshold: formData.approvalType === 'Quorum' ? formData.approvalThreshold : null,
      isActive: formData.isActive,
    });
  };

  return (
    <div>
      {/* Sélection Domaine + Entité */}
      <div className="row mb-4">
        <div className="col-md-6">
          <label className="form-label">
            Domaine <span className="text-danger">*</span>
          </label>
          <SearchableSelect
            options={domains}
            value={selectedDomain}
            onChange={setSelectedDomain}
            valueKey="id"
            labelKey="name"
            searchKeys={['name', 'email']}
            placeholder="Sélectionnez un domaine"
            className="form-select"
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">
            Type d'Entité <span className="text-danger">*</span>
          </label>
          <select
            className="form-select form-control"
            value={selectedEntity}
            onChange={(e) => setSelectedEntity(e.target.value)}
          >
            <option value="">Sélectionnez une entité</option>
            {entityTypes.map((entity) => (
              <option key={entity.value} value={entity.value}>
                {entity.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Formulaire de configuration */}
      {selectedDomain && selectedEntity && (
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">
              Configuration pour {selectedDomain.name} - {selectedEntity}
            </h5>
            <p className="card-text text-muted">
              Définissez comment les {selectedEntity.toLowerCase()}s doivent être validés dans ce domaine.
            </p>
          </div>
          <div className="card-body">
            {loadingRule ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="text-muted mt-2">Chargement de la règle...</p>
              </div>
            ) : (
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">
                    Type de Validation <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select form-control"
                    value={formData.approvalType}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      approvalType: e.target.value,
                      approvalThreshold: null 
                    })}
                  >
                    <option value="Any">Un validateur suffit</option>
                    <option value="All">Tous les validateurs</option>
                    <option value="Quorum">Quorum (seuil)</option>
                  </select>
                </div>

                {formData.approvalType === 'Quorum' && (
                  <div className="col-md-6">
                    <label className="form-label">
                      Seuil Minimum <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-control ${!formData.approvalThreshold ? 'is-invalid' : ''}`}
                      min="1"
                      value={formData.approvalThreshold || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        approvalThreshold: parseInt(e.target.value) || null 
                      })}
                      placeholder="Ex: 2"
                    />
                    <div className="form-text">
                      Nombre minimum de validateurs requis pour approuver.
                    </div>
                  </div>
                )}

                <div className="col-12 mt-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="isActive">
                      Règle active
                    </label>
                  </div>
                </div>

                <div className="col-12 mt-4">
                  <button
                    className="btn btn-success"
                    onClick={handleSaveRule}
                    disabled={saveRuleMutation.isLoading}
                  >
                    {saveRuleMutation.isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Sauvegarder la Règle
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationRulesManager;